/**
 * The one place that talks to the API.
 *
 * Every endpoint below corresponds to a route that exists in the live OpenAPI
 * document. There is deliberately **no** `runEverything()`: the backend has no
 * orchestration endpoint, and a convenience wrapper that pretended otherwise
 * would be a lie told in TypeScript rather than in pixels.
 *
 * The API does serve *derived* bytes now - artifact downloads and media
 * previews - but never originals, and every one of those routes re-checks
 * ownership. A preview URL is built by `MediaPreview` from an id, never handed
 * out by the server as a location.
 *
 * No component calls `fetch` directly. Concentrating it here is what makes the
 * error taxonomy, the timeout and the "never render `detail`" rule enforceable
 * instead of aspirational.
 */

import { apiUrl } from "./config";
import { ApiError, extractCode, extractDetail, kindForStatus } from "./errors";
import type {
  AccountSession,
  ActiveWork,
  AnalysisResult,
  CaptureSession,
  CreateCaptureSessionRequest,
  CreateProjectRequest,
  FormatOffer,
  Health,
  KeyframeSelection,
  MediaAsset,
  MediaCapabilities,
  MediaCountSummary,
  MediaEnhancement,
  ModelCapabilities,
  RegionReview,
  SceneCoverage,
  MeshHealth,
  Notification,
  PairingAvailability,
  PairingCreated,
  PairingState,
  Project,
  ProjectCoverage,
  ProjectCoverageSummary,
  ProjectListItem,
  ProjectWorkflow,
  QualityAssessment,
  ReconstructionCapability,
  ReconstructionJob,
  ReconstructionJobSummary,
  ReconstructionReadiness,
  ScaleCalibration,
  SessionUser,
  Task,
  TimelineEvent,
  UpdateProjectRequest,
  WorkspaceSummary,
} from "./types";

/** Long enough for a cold database, short enough that a dead server is noticed. */
const DEFAULT_TIMEOUT_MS = 15_000;
/**
 * Analysis endpoints run real work synchronously - face landmarks over every
 * frame of a session. They get their own budget; applying the read timeout to
 * them would abandon work that is progressing normally.
 */
const ANALYSIS_TIMEOUT_MS = 300_000;
/**
 * Artifact downloads are bytes over a link, not work on the server. Generous,
 * because a point cloud on a slow connection is a legitimate slow request, and
 * bounded, because a stalled one must not hang the screen forever.
 */
const ARTIFACT_TIMEOUT_MS = 120_000;

export interface RequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** Ties an external abort signal to our own timeout without leaking either. */
function withTimeout(
  timeoutMs: number,
  external: AbortSignal | undefined,
): { signal: AbortSignal; done: () => void; timedOut: () => boolean } {
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort();
  }, timeoutMs);
  const forward = () => controller.abort();
  external?.addEventListener("abort", forward);
  return {
    signal: controller.signal,
    done: () => {
      clearTimeout(timer);
      external?.removeEventListener("abort", forward);
    },
    timedOut: () => expired,
  };
}

async function request<T>(
  path: string,
  init: RequestInit & { query?: Record<string, string | number | undefined> } = {},
  options: RequestOptions = {},
): Promise<T> {
  const { query, ...requestInit } = init;
  const url = apiUrl(path, query);
  const timeout = withTimeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS, options.signal);

  let response: Response;
  try {
    response = await fetch(url, {
      ...requestInit,
      signal: timeout.signal,
      // The session is a cookie, so it has to be sent - and `Set-Cookie` on
      // the login response has to be honoured, which `omit` also discards.
      // `same-origin`, not `include`: development is proxied through Vite and
      // production is served behind one reverse proxy, so every request here
      // is same-origin. `include` would additionally attach the cookie to a
      // cross-origin API, which is exactly the configuration this project
      // decided not to support.
      credentials: "same-origin",
      headers: { Accept: "application/json", ...(requestInit.headers ?? {}) },
    });
  } catch (cause) {
    if (timeout.timedOut()) {
      throw new ApiError({ kind: "timeout", url, cause });
    }
    if (options.signal?.aborted) {
      throw new ApiError({ kind: "canceled", url, cause });
    }
    // A CORS rejection is indistinguishable from being offline here - the
    // browser withholds the reason on purpose. Both mean "could not reach it".
    throw new ApiError({ kind: "network", url, cause });
  } finally {
    timeout.done();
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // A non-JSON error body (a proxy's HTML 502, say) tells us nothing worth
      // keeping. The status already carried the meaning.
    }
    throw new ApiError({
      kind: kindForStatus(response.status),
      status: response.status,
      detail: extractDetail(body),
      code: extractCode(body),
      url,
    });
  }

  if (response.status === 204) return undefined as T;

  try {
    return (await response.json()) as T;
  } catch (cause) {
    throw new ApiError({ kind: "malformed", status: response.status, url, cause });
  }
}

function jsonBody(payload: unknown): RequestInit {
  return {
    method: "POST",
    body: JSON.stringify(payload),
    headers: { "Content-Type": "application/json" },
  };
}

// --- health ----------------------------------------------------------------

export const getHealth = (o?: RequestOptions) => request<Health>("/health", {}, o);

// --- notifications ---------------------------------------------------------

export const listNotifications = (
  params: { limit?: number; offset?: number } = {},
  o?: RequestOptions,
) => request<Notification[]>("/notifications", { query: params }, o);

export const getUnreadNotificationCount = (o?: RequestOptions) =>
  request<{ unread: number }>("/notifications/unread-count", {}, o);

export const markNotificationRead = (id: string, o?: RequestOptions) =>
  request<void>(`/notifications/${encodeURIComponent(id)}/read`, { method: "POST" }, o);

export const markAllNotificationsRead = (o?: RequestOptions) =>
  request<{ unread: number }>("/notifications/read-all", { method: "POST" }, o);

// --- projects --------------------------------------------------------------

export const listProjects = (
  params: { limit?: number; offset?: number } = {},
  o?: RequestOptions,
) => request<ProjectListItem[]>("/projects", { query: params }, o);

export const getMediaCapabilities = (o?: RequestOptions) =>
  request<MediaCapabilities>("/media/capabilities", {}, o);

/**
 * Which learned models this installation can run, and why it cannot run the
 * rest.
 *
 * Read by every screen that would otherwise guess. The answer is usually
 * "no model", and saying which reason is what turns a dead control into an
 * honest one.
 */
export const getModelCapabilities = (o?: RequestOptions) =>
  request<ModelCapabilities>("/capabilities/models", {}, o);

export const getReconstructionCapability = (o?: RequestOptions) =>
  request<ReconstructionCapability>("/reconstructions/capabilities", {}, o);

export const getProject = (projectId: string, o?: RequestOptions) =>
  request<Project>(`/projects/${encodeURIComponent(projectId)}`, {}, o);

export const createProject = (payload: CreateProjectRequest, o?: RequestOptions) =>
  request<Project>("/projects", jsonBody(payload), o);

/**
 * Rename a project, or change its description.
 *
 * A real persisted edit. There is no owner field to send and the backend
 * would reject one - `extra="forbid"` on the request model - so ownership is
 * derived server-side from the session and nowhere else.
 *
 * Passing `description: null` clears it; omitting the key leaves it alone.
 * The backend distinguishes the two by which fields were present in the body,
 * which is why this builds the payload rather than spreading a partial.
 */
export const updateProject = (
  projectId: string,
  payload: UpdateProjectRequest,
  o?: RequestOptions,
) =>
  request<Project>(
    `/projects/${encodeURIComponent(projectId)}`,
    { ...jsonBody(payload), method: "PATCH" },
    o,
  );

/**
 * The place, the people in it, and what to photograph next.
 *
 * Only for `human_in_environment`; every other subject answers 404, the same
 * way a room has no ear coverage.
 */
export const getSceneCoverage = (projectId: string, o?: RequestOptions) =>
  request<SceneCoverage>(
    `/projects/${encodeURIComponent(projectId)}/scene-coverage`,
    {},
    o,
  );

/**
 * Photographs whose capture angle still needs a person, oldest first.
 *
 * Works for every subject including a head - it is about capture *stations*,
 * which is a different truth from a head's semantic coverage.
 */
export const getRegionReview = (
  projectId: string,
  params: { limit?: number; offset?: number } = {},
  o?: RequestOptions,
) =>
  request<RegionReview>(
    `/projects/${encodeURIComponent(projectId)}/region-review`,
    { query: params },
    o,
  );

/**
 * Settle one photograph's capture angle.
 *
 * `null` is a real answer meaning "none of these angles", and nothing may
 * later overwrite it - a person is the highest authority there is.
 */
export const assignReviewedRegion = (
  mediaId: string,
  region: string | null,
  o?: RequestOptions,
) =>
  request<MediaAsset>(
    `/media/${encodeURIComponent(mediaId)}/region-review`,
    { ...jsonBody({ region }), method: "PATCH" },
    o,
  );

/** Where one project stands: the checklist, the next action, attention items. */
export const getProjectWorkflow = (projectId: string, o?: RequestOptions) =>
  request<ProjectWorkflow>(
    `/projects/${encodeURIComponent(projectId)}/workflow`,
    {},
    o,
  );

/** A project's history, derived from stored timestamps, newest first. */
export const getProjectTimeline = (
  projectId: string,
  params: { limit?: number } = {},
  o?: RequestOptions,
) =>
  request<TimelineEvent[]>(
    `/projects/${encodeURIComponent(projectId)}/timeline`,
    { query: params },
    o,
  );

// --- workspace -------------------------------------------------------------

/** Account-wide totals for Ana Sayfa. Takes no id: the session is the scope. */
export const getWorkspaceSummary = (o?: RequestOptions) =>
  request<WorkspaceSummary>("/workspace/summary", {}, o);

/** Reconstruction work in flight and recently finished. Bounded, user-scoped. */
export const listTasks = (
  params: { limit?: number } = {},
  o?: RequestOptions,
) => request<Task[]>("/tasks", { query: params }, o);

export const deleteCaptureSession = (sessionId: string, o?: RequestOptions) =>
  request<void>(
    `/capture-sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE" },
    o,
  );

/**
 * Delete a project.
 *
 * `force` stops the project's queued and running reconstructions first. It is
 * passed only when the customer has been shown what is running and pressed the
 * button that says so - never as a default, and never as a retry after a 409.
 * It does not lift the refusal on a project a finished reconstruction cites.
 */
export const deleteProject = (
  projectId: string,
  options: { force?: boolean } = {},
  o?: RequestOptions,
) =>
  request<void>(
    `/projects/${encodeURIComponent(projectId)}${options.force ? "?force=true" : ""}`,
    { method: "DELETE" },
    o,
  );

/** What a deletion would stop. Empty is the ordinary answer. */
export const getProjectActiveWork = (projectId: string, o?: RequestOptions) =>
  request<ActiveWork[]>(`/projects/${encodeURIComponent(projectId)}/active-work`, {}, o);

// --- accounts --------------------------------------------------------------

export const login = (
  payload: { email: string; password: string; remember?: boolean },
  o?: RequestOptions,
) => request<SessionUser>("/auth/login", jsonBody(payload), o);

export const updateDisplayName = (displayName: string, o?: RequestOptions) =>
  request<SessionUser>(
    "/auth/me",
    { method: "PATCH", body: JSON.stringify({ display_name: displayName }), headers: { "Content-Type": "application/json" } },
    o,
  );

export const changePassword = (
  payload: { current_password: string; new_password: string },
  o?: RequestOptions,
) => request<{ other_sessions_revoked: number }>("/auth/password", jsonBody(payload), o);

export const requestPasswordReset = (email: string, o?: RequestOptions) =>
  request<{ message: string }>("/auth/password-reset/request", jsonBody({ email }), o);

export const verifyPasswordReset = (
  payload: { email: string; code: string },
  o?: RequestOptions,
) => request<{ reset_token: string }>("/auth/password-reset/verify", jsonBody(payload), o);

export const completePasswordReset = (
  payload: { reset_token: string; new_password: string },
  o?: RequestOptions,
) => request<void>("/auth/password-reset/complete", jsonBody(payload), o);

export const listSessions = (o?: RequestOptions) =>
  request<{ sessions: AccountSession[] }>("/auth/sessions", {}, o);

export const revokeSession = (sessionId: string, o?: RequestOptions) =>
  request<void>(`/auth/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" }, o);

export const revokeOtherSessions = (o?: RequestOptions) =>
  request<{ other_sessions_revoked: number }>(
    "/auth/sessions/revoke-others",
    { method: "POST" },
    o,
  );

export const logout = (o?: RequestOptions) =>
  request<void>("/auth/logout", { method: "POST" }, o);

export const getRegistrationStatus = (o?: RequestOptions) =>
  request<{ open: boolean }>("/auth/registration", {}, o);

export const register = (
  payload: { email: string; password: string; display_name: string },
  o?: RequestOptions,
) => request<SessionUser>("/auth/register", jsonBody(payload), o);

export const getCurrentUser = (o?: RequestOptions) =>
  request<SessionUser>("/auth/me", {}, o);

// --- reconstruction --------------------------------------------------------

export const getLatestReconstruction = (projectId: string, o?: RequestOptions) =>
  request<ReconstructionJob>(
    `/projects/${encodeURIComponent(projectId)}/reconstruction`,
    {},
    o,
  );

export const listReconstructions = (projectId: string, o?: RequestOptions) =>
  request<ReconstructionJobSummary[]>(
    `/projects/${encodeURIComponent(projectId)}/reconstructions`,
    {},
    o,
  );

/**
 * What the project still needs before a solve can run.
 *
 * Cheap and read-only - it counts rows, it does not decode anything - so the
 * reconstruction screen asks on open and again after preparation.
 */
export const getReconstructionReadiness = (projectId: string, o?: RequestOptions) =>
  request<ReconstructionReadiness>(
    `/projects/${encodeURIComponent(projectId)}/reconstruction-readiness`,
    {},
    o,
  );

/**
 * One artifact's bytes, as an `ArrayBuffer`.
 *
 * Deliberately not routed through `request`, which parses JSON. A point cloud
 * is binary and can be megabytes: decoding it as text, or base64-ing it, would
 * cost several copies of the whole file for nothing. The buffer is handed
 * straight to the parser and never stored in React state.
 *
 * `byte_size` from the artifact metadata is checked *before* this is called -
 * see `MAX_VIEWABLE_BYTES` - so this does not defend against a huge download
 * on its own.
 */
export async function getArtifactContent(
  jobId: string,
  artifactId: string,
  options: RequestOptions = {},
): Promise<ArrayBuffer> {
  const url = apiUrl(
    `/reconstructions/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(
      artifactId,
    )}/content`,
  );
  const timeout = withTimeout(options.timeoutMs ?? ARTIFACT_TIMEOUT_MS, options.signal);
  let response: Response;
  try {
    response = await fetch(url, {
      signal: timeout.signal,
      credentials: "same-origin",
      headers: { Accept: "application/octet-stream" },
    });
  } catch (cause) {
    if (timeout.timedOut()) throw new ApiError({ kind: "timeout", url, cause });
    if (options.signal?.aborted) throw new ApiError({ kind: "canceled", url, cause });
    throw new ApiError({ kind: "network", url, cause });
  } finally {
    timeout.done();
  }

  if (!response.ok) {
    // No body is read on failure: an error response to a binary endpoint has
    // nothing this client is entitled to render anyway.
    throw new ApiError({
      kind: kindForStatus(response.status),
      status: response.status,
      url,
    });
  }
  return response.arrayBuffer();
}

/**
 * One binary download from the export routes, through the session.
 *
 * Deliberately the same shape as `getArtifactContent`: same credentials, same
 * timeout, same refusal to read an error body. A derived format is not a
 * different kind of door, and giving it its own gentler fetch is how the two
 * paths drift until one of them stops re-checking ownership.
 */
async function fetchExportBytes(url: string, options: RequestOptions = {}) {
  const timeout = withTimeout(options.timeoutMs ?? ARTIFACT_TIMEOUT_MS, options.signal);
  let response: Response;
  try {
    response = await fetch(url, {
      signal: timeout.signal,
      credentials: "same-origin",
      headers: { Accept: "application/octet-stream" },
    });
  } catch (cause) {
    if (timeout.timedOut()) throw new ApiError({ kind: "timeout", url, cause });
    if (options.signal?.aborted) throw new ApiError({ kind: "canceled", url, cause });
    throw new ApiError({ kind: "network", url, cause });
  } finally {
    timeout.done();
  }
  if (!response.ok) {
    throw new ApiError({
      kind: kindForStatus(response.status),
      status: response.status,
      url,
    });
  }
  return response.arrayBuffer();
}

export const listReconstructionExports = (jobId: string, o?: RequestOptions) =>
  request<FormatOffer[]>(
    `/reconstructions/${encodeURIComponent(jobId)}/exports`,
    { method: "GET" },
    o,
  );

export const getMeshHealth = (jobId: string, o?: RequestOptions) =>
  request<MeshHealth>(
    `/reconstructions/${encodeURIComponent(jobId)}/mesh-health`,
    { method: "GET" },
    o,
  );

export const getReconstructionExport = (
  jobId: string,
  format: string,
  o?: RequestOptions,
) =>
  fetchExportBytes(
    apiUrl(
      `/reconstructions/${encodeURIComponent(jobId)}/exports/${encodeURIComponent(
        format,
      )}/content`,
    ),
    o,
  );

export const getReconstructionBundle = (jobId: string, o?: RequestOptions) =>
  fetchExportBytes(
    apiUrl(`/reconstructions/${encodeURIComponent(jobId)}/exports/bundle/content`),
    o,
  );

export const getScaleCalibration = (jobId: string, o?: RequestOptions) =>
  request<ScaleCalibration>(
    `/reconstructions/${encodeURIComponent(jobId)}/scale`,
    { method: "GET" },
    o,
  );

export const setScaleCalibration = (
  jobId: string,
  body: {
    point_a: [number, number, number];
    point_b: [number, number, number];
    reference_length: number;
    reference_unit: string;
    model_diagonal: number;
  },
  o?: RequestOptions,
) =>
  request<ScaleCalibration>(
    `/reconstructions/${encodeURIComponent(jobId)}/scale`,
    { method: "PUT", body: JSON.stringify(body) },
    o,
  );

export const clearScaleCalibration = (jobId: string, o?: RequestOptions) =>
  request<void>(
    `/reconstructions/${encodeURIComponent(jobId)}/scale`,
    { method: "DELETE" },
    o,
  );

export const createReconstruction = (
  projectId: string,
  params: { profile?: string } = {},
  o?: RequestOptions,
) =>
  request<ReconstructionJob>(
    `/projects/${encodeURIComponent(projectId)}/reconstructions`,
    { method: "POST", query: params },
    o,
  );

// --- phone pairing ---------------------------------------------------------

export const getPairingAvailability = (o?: RequestOptions) =>
  request<PairingAvailability>("/phone-pairings/availability", {}, o);

export const createPhonePairing = (
  projectId: string,
  payload: { capture_session_id?: string; ttl_seconds?: number } = {},
  o?: RequestOptions,
) =>
  request<PairingCreated>(
    `/projects/${encodeURIComponent(projectId)}/phone-pairings`,
    jsonBody(payload),
    o,
  );

export const getPhonePairing = (pairingId: string, o?: RequestOptions) =>
  request<PairingState>(
    `/phone-pairings/${encodeURIComponent(pairingId)}`,
    {},
    o,
  );

export const revokePhonePairing = (pairingId: string, o?: RequestOptions) =>
  request<PairingState>(
    `/phone-pairings/${encodeURIComponent(pairingId)}/revoke`,
    { method: "POST" },
    o,
  );

// --- capture sessions ------------------------------------------------------

export const listCaptureSessions = (
  projectId: string,
  params: { limit?: number; offset?: number } = {},
  o?: RequestOptions,
) =>
  request<CaptureSession[]>(
    `/projects/${encodeURIComponent(projectId)}/capture-sessions`,
    { query: params },
    o,
  );

export const createCaptureSession = (
  projectId: string,
  payload: CreateCaptureSessionRequest,
  o?: RequestOptions,
) =>
  request<CaptureSession>(
    `/projects/${encodeURIComponent(projectId)}/capture-sessions`,
    jsonBody(payload),
    o,
  );

export const getCaptureSession = (sessionId: string, o?: RequestOptions) =>
  request<CaptureSession>(`/capture-sessions/${encodeURIComponent(sessionId)}`, {}, o);

/**
 * Declare that no more media is coming.
 *
 * A statement about the capture, not about analysis: analyses keep their own
 * versions and can be re-run afterwards. Idempotent server-side, so a double
 * click is harmless.
 */
export const finalizeCaptureSession = (sessionId: string, o?: RequestOptions) =>
  request<CaptureSession>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/finalize`,
    { method: "POST" },
    o,
  );

// --- media -----------------------------------------------------------------

export const listSessionMedia = (
  sessionId: string,
  params: { limit?: number; offset?: number } = {},
  o?: RequestOptions,
) =>
  request<MediaAsset[]>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/media`,
    { query: params },
    o,
  );

/** The largest page the API will return in one request. */
const MEDIA_PAGE_LIMIT = 200;

/**
 * Every file in a session, not the first page of them.
 *
 * The screens page deliberately - a grid renders a hundred cards, not a
 * thousand - but *acting on* a session is a different question from
 * *rendering* one. Running the analysis over `assets` meant running it over
 * whatever the grid had asked for: a 150-file session was measured 100 files
 * deep and then badged "Tamamlandı".
 *
 * Pages until a short page comes back, so it cannot silently stop at a
 * boundary the way `offset + limit` arithmetic does when a file is deleted
 * mid-walk.
 */
export const listAllSessionMedia = async (
  sessionId: string,
  o?: RequestOptions,
): Promise<MediaAsset[]> => {
  const all: MediaAsset[] = [];
  for (;;) {
    const page = await listSessionMedia(
      sessionId,
      { limit: MEDIA_PAGE_LIMIT, offset: all.length },
      o,
    );
    all.push(...page);
    if (page.length < MEDIA_PAGE_LIMIT) return all;
  }
};

/**
 * Upload one file, reporting real progress.
 *
 * `XMLHttpRequest` rather than `fetch` for one reason: `fetch` cannot report
 * upload progress, and a progress bar that animates on a timer while a 400 MB
 * video crawls over Wi-Fi is a lie the user can feel. This is the only place
 * in the app that does not go through `request`, and it reproduces the same
 * error taxonomy deliberately.
 *
 * The frontend checks the extension before calling this. That check is for the
 * user's benefit - the server's allow-list is the one that decides.
 */
export function uploadMedia(
  sessionId: string,
  file: File,
  handlers: {
    onProgress?: (fraction: number) => void;
    signal?: AbortSignal;
  } = {},
  guidedCoverageRegion?: string | null,
): Promise<MediaAsset> {
  const url = apiUrl(`/capture-sessions/${encodeURIComponent(sessionId)}/media`);

  return new Promise<MediaAsset>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.responseType = "text";
    xhr.setRequestHeader("Accept", "application/json");
    // Content-Type is left to the browser so it can add the multipart boundary.

    let canceled = false;

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        handlers.onProgress?.(event.loaded / event.total);
      }
    });

    xhr.addEventListener("load", () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        if (body && typeof body === "object") {
          resolve(body as MediaAsset);
        } else {
          reject(new ApiError({ kind: "malformed", status: xhr.status, url }));
        }
        return;
      }
      reject(
        new ApiError({
          kind: kindForStatus(xhr.status),
          status: xhr.status,
          detail: extractDetail(body),
          // The refusal code, which is the only thing that distinguishes a
          // renamed PNG from a corrupt file - both 422, opposite remedies.
          // Dropping it here was why a rejected upload said "check the
          // fields" on a screen with no fields.
          code: extractCode(body),
          url,
        }),
      );
    });

    xhr.addEventListener("error", () => {
      reject(new ApiError({ kind: "network", url }));
    });
    xhr.addEventListener("timeout", () => {
      reject(new ApiError({ kind: "timeout", url }));
    });
    xhr.addEventListener("abort", () => {
      reject(new ApiError({ kind: canceled ? "canceled" : "network", url }));
    });

    if (handlers.signal) {
      if (handlers.signal.aborted) {
        reject(new ApiError({ kind: "canceled", url }));
        return;
      }
      handlers.signal.addEventListener("abort", () => {
        canceled = true;
        xhr.abort();
      });
    }

    const form = new FormData();
    form.append("file", file, file.name);
    if (guidedCoverageRegion) form.append("guided_coverage_region", guidedCoverageRegion);
    xhr.send(form);
  });
}

// --- keyframe selection ----------------------------------------------------

/**
 * Choose which frames of a video are worth analysing.
 *
 * This is per media asset, not per session, and it is a **prerequisite** for
 * face analysis on any session containing video: the session analysis refuses
 * to run with "no keyframe selection" until it has been done. That was found
 * by running the real chain rather than by reading the routes, which is why
 * the UI shows it as its own step instead of discovering it as a 409.
 */
export const runKeyframeSelection = (
  mediaAssetId: string,
  /**
   * What the frames are for, and therefore how they are chosen.
   *
   * `analysis` keeps frames that look *unlike* each other, which is what face
   * measurement wants. `reconstruction` keeps the overlap between neighbouring
   * frames, which is the only thing feature matching can work with. A run of
   * one kind is not usable as the other, and reconstruction ignores analysis
   * runs rather than reusing them.
   *
   * Defaults to `analysis`, which is what the existing analysis chain needs.
   */
  purpose: "analysis" | "reconstruction" = "analysis",
  o?: RequestOptions,
) =>
  request<KeyframeSelection>(
    `/media/${encodeURIComponent(mediaAssetId)}/keyframe-selection`,
    { method: "POST", query: { purpose } },
    // Frame extraction and scoring over a long video is minutes of real work.
    { timeoutMs: ANALYSIS_TIMEOUT_MS, ...o },
  );

/** Delete one uploaded file, its derived data and its stored bytes. */
export const deleteMedia = (mediaAssetId: string, o?: RequestOptions) =>
  request<void>(
    `/media/${encodeURIComponent(mediaAssetId)}`,
    { method: "DELETE" },
    o,
  );

export const getKeyframeSelection = (mediaAssetId: string, o?: RequestOptions) =>
  request<KeyframeSelection>(
    `/media/${encodeURIComponent(mediaAssetId)}/keyframe-selection`,
    {},
    o,
  );

// --- generic media quality -------------------------------------------------

export const runQualityAssessment = (mediaAssetId: string, o?: RequestOptions) =>
  request<QualityAssessment>(
    `/media/${encodeURIComponent(mediaAssetId)}/quality-assessment`,
    { method: "POST" },
    analysisOptions(o),
  );

export const getQualityAssessment = (mediaAssetId: string, o?: RequestOptions) =>
  request<QualityAssessment>(
    `/media/${encodeURIComponent(mediaAssetId)}/quality-assessment`,
    {},
    o,
  );

// --- per-session analysis steps --------------------------------------------
// Separate calls because the backend exposes separate endpoints. The UI runs
// them in order and shows each one's outcome; it does not present them as a
// single button, because a single button would imply a transaction the backend
// does not offer - if step three fails, steps one and two remain done.

const analysisOptions = (o?: RequestOptions): RequestOptions => ({
  timeoutMs: ANALYSIS_TIMEOUT_MS,
  ...o,
});

export const runFaceAnalysis = (sessionId: string, o?: RequestOptions) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/face-analysis`,
    { method: "POST" },
    analysisOptions(o),
  );

export const getFaceAnalysis = (sessionId: string, o?: RequestOptions) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/face-analysis`,
    {},
    o,
  );

export const runHumanAnalysis = (
  sessionId: string,
  params: { face_analysis_version?: number } = {},
  o?: RequestOptions,
) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/human-analysis`,
    { method: "POST", query: params },
    analysisOptions(o),
  );

export const getHumanAnalysis = (sessionId: string, o?: RequestOptions) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/human-analysis`,
    {},
    o,
  );

export const runHeadCoverage = (
  sessionId: string,
  params: { human_analysis_version?: number; profile?: string } = {},
  o?: RequestOptions,
) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/head-coverage`,
    { method: "POST", query: params },
    analysisOptions(o),
  );

export const getHeadCoverage = (sessionId: string, o?: RequestOptions) =>
  request<AnalysisResult>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/head-coverage`,
    {},
    o,
  );

// --- project coverage ------------------------------------------------------

export const runProjectCoverage = (
  projectId: string,
  params: { profile?: string } = {},
  o?: RequestOptions,
) =>
  request<ProjectCoverage>(
    `/projects/${encodeURIComponent(projectId)}/coverage`,
    { method: "POST", query: params },
    analysisOptions(o),
  );

export const getProjectCoverage = (
  projectId: string,
  params: { version?: number } = {},
  o?: RequestOptions,
) =>
  request<ProjectCoverage>(
    `/projects/${encodeURIComponent(projectId)}/coverage`,
    { query: params },
    o,
);

export const getViewpointCoverage = (projectId: string, o?: RequestOptions) =>
  request<import("./types").ViewpointCoverage>(
    `/projects/${encodeURIComponent(projectId)}/viewpoint-coverage`, {}, o,
  );

/** What one estimation run did. Counted from what was written, not predicted. */
export interface RegionEstimateReport {
  settled: number;
  considered: number;
  review_required: number;
  undetermined: number;
  assigned: number;
  anchors_missing: boolean;
  available: boolean;
  auto_assignment_enabled?: boolean;
  protected?: number;
  evaluated?: number;
  anchor_regions?: number;
  tentative?: number;
}

export const estimateProjectRegions = (projectId: string, o?: RequestOptions, mediaIds?: readonly string[], reviewCleared = false, acceptSuggestions = false) =>
  request<RegionEstimateReport>(
    `/projects/${encodeURIComponent(projectId)}/region-estimates`,
    { method: "POST", ...(mediaIds ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify({ media_ids: mediaIds, ...(reviewCleared ? { review_cleared: true } : {}), ...(acceptSuggestions ? { accept_suggestions: true } : {}) }) } : {}) },
    // The analysis budget, because this is an analysis endpoint: it encodes
    // every unmarked photograph with DINOv2 in the request. Measured at 24.8 s
    // for a 150-photograph session on the development machine, which the
    // 15 s read timeout cut off - and the failure was invisible, because the
    // screen quite correctly treats a lost suggestion as nothing to shout
    // about. It cost an afternoon to find for exactly that reason.
    { timeoutMs: ANALYSIS_TIMEOUT_MS, ...o },
  );

export const getSessionMediaSummary = (sessionId: string, o?: RequestOptions) =>
  request<MediaCountSummary>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/media-summary`, {}, o,
  );

export const getSessionQualitySummary = (sessionId: string, o?: RequestOptions) =>
  request<import("./types").SessionQualitySummary>(
    `/capture-sessions/${encodeURIComponent(sessionId)}/quality-summary`, {}, o,
  );

export const getGuidedCoverage = (projectId: string, o?: RequestOptions) =>
  request<import("./types").GuidedCoverage>(
    `/projects/${encodeURIComponent(projectId)}/guided-coverage`, {}, o,
  );

/**
 * The project's media, optionally narrowed to one capture angle.
 *
 * Exists so the coverage screen can *act* rather than only report: it is a
 * project-level screen and media is stored per capture session, so listing
 * files any other way would mean a request per session.
 */
export const listGuidedCoverageMedia = (
  projectId: string,
  params: { region?: string; limit?: number; offset?: number } = {},
  o?: RequestOptions,
) =>
  request<MediaAsset[]>(
    `/projects/${encodeURIComponent(projectId)}/guided-coverage/media`,
    { query: params },
    o,
  );

export const assignGuidedCoverageRegion = (mediaAssetId: string, region: string | null, o?: RequestOptions) =>
  request<import("./types").GuidedCoverage>(
    `/media/${encodeURIComponent(mediaAssetId)}/guided-coverage-region`,
    { method: "PATCH", body: JSON.stringify({ region }), headers: { "Content-Type": "application/json" } }, o,
  );

export const enhanceMedia = (mediaAssetId: string, o?: RequestOptions) =>
  request<MediaEnhancement>(`/media/${encodeURIComponent(mediaAssetId)}/enhancements`, { method: "POST" }, analysisOptions(o));

/** Super resolution at a chosen factor. Minutes on CPU, so never awaited by a
 *  screen that blocks on it - see `ActiveWorkStrip` for how work in flight is
 *  reported. The derivative it returns is inadmissible to reconstruction. */
export const upscaleMedia = (mediaAssetId: string, scale: number, o?: RequestOptions) =>
  request<MediaEnhancement>(
    `/media/${encodeURIComponent(mediaAssetId)}/super-resolution`,
    {
      method: "POST",
      body: JSON.stringify({ scale }),
      headers: { "Content-Type": "application/json" },
    },
    analysisOptions(o),
  );

export const listProjectCoverages = (projectId: string, o?: RequestOptions) =>
  request<ProjectCoverageSummary[]>(
    `/projects/${encodeURIComponent(projectId)}/coverages`,
    {},
    o,
  );
